<script setup lang="ts">
import { ref, computed } from 'vue'

interface Props {
  src: string
  alt: string
  class?: string
  /** Use thumbnail variant (smaller size, for list views) */
  thumbnail?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  thumbnail: false
})

const hasError = ref(false)
const isLoaded = ref(false)

const handleError = () => {
  hasError.value = true
}

const handleLoad = () => {
  isLoaded.value = true
}

/**
 * Get the image URL, converting optimized paths to thumbnail if needed
 */
const imageUrl = computed(() => {
  if (!props.src) return ''

  // If thumbnail requested and src is a local optimized image, use thumbnail version
  if (props.thumbnail && props.src.includes('/images/fighters/optimized/')) {
    return props.src.replace('/optimized/', '/thumbnails/')
  }

  return props.src
})

</script>

<template>
  <div class="relative w-full h-full bg-gray-700 overflow-hidden">
    <div
      v-if="!hasError && !isLoaded"
      class="absolute inset-0 bg-gray-600 animate-pulse"
    />
    <img
      v-if="!hasError && imageUrl"
      :src="imageUrl"
      :alt="props.alt"
      :class="[props.class, { 'opacity-0': !isLoaded, 'opacity-100': isLoaded }]"
      class="w-full h-full object-cover transition-opacity duration-300"
      loading="lazy"
      decoding="async"
      @error="handleError"
      @load="handleLoad"
    />
    <div
      v-else
      class="w-full h-full flex items-center justify-center"
      :title="props.alt"
      role="img"
      :aria-label="`Fighter photo unavailable: ${props.alt}`"
    >
      <svg
        class="w-1/3 h-1/3 text-gray-300"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
      </svg>
    </div>
  </div>
</template>
